import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material';
import { Dispatch, FC, Fragment, SetStateAction, useState } from 'react';

import { Piece, PieceAction } from '@apiContract';
import Divider from '@components/Divider';
import Footer from '@components/DrawerContent/Footer';
import Title from '@components/DrawerContent/Title';
import { useMailpiece } from '@hooks/useMailpiece';
import { MailPieceInboxType } from '@rdTypes/shared';

import { useMailpieceActions } from '../hooks/useMailpieceActions';
import { mailpieceActions } from '../index';
import { getActionDisplayPriority } from '../utils';
import { sxActionButton, sxActionIcon, sxActionItem, sxActionsList, sxDrawerPaper, sxP20 } from './style';

type Props = {
  open?: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  pieces: Piece[];
  access: MailPieceInboxType;
  pendingSearchValue?: string;
};

const DrawerContent: FC<Props> = ({ setOpen, pieces, access, pendingSearchValue }) => {
  const [selectedAction, setSelectedAction] = useState<PieceAction | null>(null);
  const [currentAction, setCurrentAction] = useState<PieceAction | null>(null);
  let Component;

  const { availableForceActions: unorderedForceActions } = useMailpieceActions(pieces, access);

  const isDifferentOrNoRecipient = pieces.some(
    (piece) => !piece.recipient?.userId || piece.recipient?.userId !== pieces?.[0].recipient?.userId,
  );

  const isGroup = pieces[0].recipient?.groupId;

  const availableForceActions = [...unorderedForceActions].sort(
    (a, b) => getActionDisplayPriority(a.action) - getActionDisplayPriority(b.action),
  );

  const { getForceActionTitle } = useMailpiece();
  const forceActionTitle = getForceActionTitle(pieces);

  const handleClose = () => {
    setOpen(false);
    setSelectedAction(null);
    setCurrentAction(null);
  };

  const handleActionSelect = (action: PieceAction) => {
    setSelectedAction(selectedAction === action ? null : action);
  };

  const startAction = async () => {
    if (!selectedAction) return;
    setCurrentAction(selectedAction);
  };

  const handleExecute = () => {
    if (!selectedAction) return;

    const actionConfig = mailpieceActions[selectedAction];
    if (!actionConfig) return;

    void startAction();
  };

  const isSinglePiece = pieces.length === 1;

  const EmptyState = () => (
    <Box sx={{ p: 3, textAlign: 'center' }}>
      <Typography variant="body2" color="text.secondary">
        No force requests available
      </Typography>
    </Box>
  );

  if (currentAction) {
    const actionConfig = mailpieceActions[currentAction];
    if (actionConfig?.Component) {
      Component = actionConfig?.Component;
    }
  }

  const ActionItem = ({
    action,
    isAvailableForAllPieces,
  }: {
    action: PieceAction;
    isAvailableForAllPieces: boolean;
  }) => {
    const actionConfig = mailpieceActions[action];
    if (!actionConfig) return null;

    const { title, Icon, actionTriggerColor } = actionConfig;
    let isDisabled = !isSinglePiece && !isAvailableForAllPieces;
    const isSelected = selectedAction === action;
    let tooltipTitle = isDisabled ? 'Not available for some selected items' : '';

    // Special behaviors for RequestForward actions diasble if differenet recipients or recipient is group
    // if you in future add second special action if consider to write some validate function
    // now left as is
    if (action === PieceAction.RequestForward && (isDifferentOrNoRecipient || isGroup)) {
      isDisabled = true;
      tooltipTitle = isGroup
        ? 'Not available when the recipient is a group'
        : 'Selected items must have the same recipient';
    }

    return (
      <Fragment key={action}>
        {action === PieceAction.Delete && <Divider sx={{ my: 1 }} dashed />}

        <Box sx={{ mb: 1, mx: 1 }}>
          <Tooltip title={tooltipTitle} placement="bottom" disableHoverListener={!isDisabled} arrow>
            <ListItem disablePadding sx={sxActionItem(isSelected)}>
              <ListItemButton
                onClick={() => handleActionSelect(action)}
                disabled={isDisabled || currentAction !== null}
                sx={sxActionButton(actionTriggerColor, isDisabled)}
              >
                <ListItemIcon sx={sxActionIcon}>
                  <Icon color="inherit" />
                </ListItemIcon>

                <ListItemText primary={typeof title === 'function' ? title(pieces[0]) : title} />
              </ListItemButton>
            </ListItem>
          </Tooltip>
        </Box>
      </Fragment>
    );
  };

  const ActionsList = () => (
    <Box sx={sxActionsList}>
      {availableForceActions.length === 0 ? (
        <EmptyState />
      ) : (
        <List disablePadding>
          {availableForceActions.map((actionData) => (
            <ActionItem key={actionData.action} {...actionData} />
          ))}
        </List>
      )}
    </Box>
  );

  return (
    <>
      <Title title={forceActionTitle} onClose={handleClose} />
      <ActionsList />
      <Footer
        sx={sxP20}
        disabled={!selectedAction || currentAction !== null}
        title={forceActionTitle}
        handleSubmit={handleExecute}
      />
      {Component && (
        <Component
          key={currentAction}
          mailpieces={pieces}
          actionStarted
          onActionFinish={() => {
            setCurrentAction(null);
            handleClose();
          }}
          pendingSearchValue={pendingSearchValue}
          isForce
        />
      )}
    </>
  );
};

const ForceActionsDrawer: FC<Props> = ({ open, ...rest }) => {
  const { setOpen } = rest;
  return (
    <Drawer open={open} anchor="right" onClose={() => setOpen(false)} slotProps={{ paper: sxDrawerPaper }}>
      {open ? <DrawerContent {...rest} /> : null}
    </Drawer>
  );
};

export default ForceActionsDrawer;
